import React from 'react';
import { LAST_UPDATED_DATE, LEGAL_TODOS } from './legalPlaceholders';

const PrivacyPolicy = () => (
  <main className="legal-page">
    <h1>Privacy Policy</h1>
    <p>
      <strong>Last updated:</strong> {LAST_UPDATED_DATE}
    </p>
    <p>
      This Privacy Policy explains how 101Setups collects, uses, stores, and shares personal data when you visit the
      website, create an account, submit content, subscribe to the newsletter, contact us, or otherwise interact with
      the service.
    </p>

    <h2>1. Controller</h2>
    <p>The controller of personal data processed through 101Setups is:</p>
    <ul>
      <li>{LEGAL_TODOS.operatorName}</li>
      <li>
        <strong>Postal address:</strong> {LEGAL_TODOS.postalAddress}
      </li>
      <li>
        <strong>Email for privacy requests:</strong> {LEGAL_TODOS.legalContactEmail}
      </li>
      <li>
        <strong>Tax ID / NIF / VAT:</strong> {LEGAL_TODOS.taxId}
      </li>
    </ul>

    <h2>2. Personal data we may collect</h2>
    <p>Depending on how you use 101Setups, we may collect and process the following categories of personal data:</p>
    <ul>
      <li>Account and profile data, such as email address, username, display name, and account-related identifiers.</li>
      <li>
        Authentication data needed to manage sign-in and account security. Authentication credentials are handled
        through our authentication provider and are not displayed publicly by default.
      </li>
      <li>
        User-submitted content and related metadata, such as setup images, product tags, titles, descriptions,
        timestamps, and other materials you choose to upload or publish.
      </li>
      <li>Communications data, such as messages or requests you send to us.</li>
      <li>Newsletter subscription data, such as your email address and any name you choose to provide when subscribing.</li>
      <li>
        Technical and usage data, such as IP address, browser or device information, browser language or similar device
        settings, app and server logs, and similar security or diagnostic data generated when you access the service.
      </li>
      <li>Analytics data, but only if you have consented to optional analytics technologies.</li>
      <li>Consent and preference records, including your analytics consent choice.</li>
    </ul>
    <p>
      We do not intentionally ask users to provide special category data through the normal use of the service. Please
      do not upload or send sensitive personal data unless it is strictly necessary and you have a clear legal basis to
      do so.
    </p>

    <h2>3. How and why we use personal data</h2>
    <p>We may process personal data for the following purposes and legal bases:</p>
    <ul>
      <li>
        To provide, operate, and secure the website and user accounts, including authentication, hosting, storage, and
        technical administration. Legal basis: performance of a contract, pre-contractual steps at your request, and/or
        our legitimate interest in operating and securing the service.
      </li>
      <li>
        To host, display, moderate, and manage user-submitted content. Legal basis: performance of a contract and/or
        our legitimate interest in operating a user-content platform responsibly.
      </li>
      <li>
        To respond to enquiries, support requests, and legal notices. Legal basis: our legitimate interest and, where
        relevant, compliance with legal obligations.
      </li>
      <li>To send newsletters and similar email communications that you have requested. Legal basis: your consent.</li>
      <li>To measure and improve website performance and usage through optional analytics. Legal basis: your consent.</li>
      <li>
        To prevent abuse, fraud, unauthorised access, and other security incidents, and to enforce our legal terms.
        Legal basis: our legitimate interest and, where applicable, compliance with legal obligations.
      </li>
      <li>
        To comply with applicable law, lawful requests from authorities, and accounting, tax, or record-keeping
        obligations. Legal basis: compliance with a legal obligation.
      </li>
    </ul>

    <h2>4. Recipients and service providers</h2>
    <p>
      We may share personal data, strictly as necessary, with service providers and recipients that help us operate
      101Setups. These may include:
    </p>
    <ul>
      <li>Amazon Web Services and related AWS services for hosting, storage, authentication, APIs, and infrastructure.</li>
      <li>Google, if and only if you consent to optional Google Analytics measurement.</li>
      <li>Kit / ConvertKit, if you subscribe to the newsletter.</li>
      <li>
        Professional advisers, hosting or technical support providers, and authorities where disclosure is required by
        law or reasonably necessary to protect our rights.
      </li>
    </ul>
    <p>
      If you click a third-party or affiliate link, including an Amazon link, the destination site will process personal
      data under its own privacy policy and terms. 101Setups is not responsible for the privacy practices of
      third-party sites.
    </p>

    <h2>5. International transfers</h2>
    <p>
      Some service providers used by 101Setups may process personal data outside the European Economic Area, including
      in the United Kingdom and the United States. Where required, we rely on an appropriate transfer mechanism under
      applicable data protection law, such as an adequacy decision, standard contractual clauses, or another lawful
      safeguard. You can contact us if you want more information about the transfer safeguards relevant to your data.
    </p>

    <h2>6. Data retention</h2>
    <p>
      We keep personal data only for as long as necessary for the purposes described in this Privacy Policy, unless a
      longer period is required or permitted by law.
    </p>
    <p>In general:</p>
    <ul>
      <li>
        Account data is kept while your account remains active and for a limited period afterwards to handle security,
        backups, disputes, and legal obligations.
      </li>
      <li>
        User-submitted content is kept while it remains published or stored in the service, and may remain in backups
        for a limited period after deletion.
      </li>
      <li>
        Newsletter subscription data is kept until you unsubscribe or we otherwise stop sending the newsletter, except
        that we may keep limited suppression information to ensure we respect an unsubscribe request.
      </li>
      <li>
        Analytics and technical records are kept for the period reasonably necessary for measurement, security,
        troubleshooting, and legal compliance.
      </li>
      <li>
        Consent records and preference data are kept for as long as necessary to demonstrate compliance and respect your
        choices.
      </li>
    </ul>

    <h2>7. Your rights</h2>
    <p>Under applicable data protection law, you may have the right to:</p>
    <ul>
      <li>request access to your personal data;</li>
      <li>request rectification of inaccurate or incomplete data;</li>
      <li>request erasure of your personal data;</li>
      <li>request restriction of processing;</li>
      <li>object to certain processing based on legitimate interests;</li>
      <li>
        request portability of data you provided to us where processing is based on consent or contract and carried out
        by automated means;
      </li>
      <li>
        withdraw consent at any time where processing is based on consent, without affecting the lawfulness of
        processing carried out before withdrawal; and
      </li>
      <li>
        lodge a complaint with the Spanish Data Protection Authority (Agencia Española de Protección de Datos, AEPD) or
        another competent supervisory authority.
      </li>
    </ul>
    <p>
      To exercise your rights, contact us at {LEGAL_TODOS.legalContactEmail}. We may need to verify your identity
      before responding.
    </p>

    <h2>8. Minors</h2>
    <p>
      101Setups is not directed to children under 14. If you are under 14, please do not create an account, subscribe,
      or send us personal data without the authorisation of your parent or legal guardian. If you believe a child has
      provided personal data unlawfully, please contact us so we can review and, where appropriate, delete it.
    </p>

    <h2>9. Security</h2>
    <p>
      We take reasonable technical and organisational measures designed to protect personal data against accidental or
      unlawful destruction, loss, alteration, unauthorised disclosure, or access. However, no internet-based service
      can guarantee absolute security.
    </p>

    <h2>10. Changes to this Privacy Policy</h2>
    <p>We may update this Privacy Policy from time to time. The version published on this page is the one currently in force.</p>

    <h2>11. Contact</h2>
    <ul>
      <li>{LEGAL_TODOS.operatorName}</li>
      <li>{LEGAL_TODOS.legalContactEmail}</li>
      <li>{LEGAL_TODOS.postalAddress}</li>
    </ul>
  </main>
);

export default PrivacyPolicy;
